/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import StringResources from './nls/StringResources';
import CrosshairsPopOver from './popover/CrosshairsPopOver';
import React from 'react';
import ReactDOM from 'react-dom';

/**
 * The Crosshairs view controller is used to interact with the liveWidget view
 * and event handlers.
 **/
const lineColor = '#C0BFC0';	//The lineColor as specified by UE.
const MAX_CAPTIONS = 4;			//The maximum captions to generate before we start generating 'n of m items'

class CrosshairsViewCtrl {
	constructor(feature, widgetApi, dashboardApi) {
		this.widgetApi = widgetApi;
		this.dashboardApi = dashboardApi;
		this.feature = feature;

		//Any callbacks registered with activate will be called on move.
		this._onMoveCrosshairsHandlers=[];

		//Internal handlers
		this._widgetApiOnMoveListener=null;
	}
	/**
	 * check if the compare line is enabled. The compare line is only disabled when the flag is set to false specifically.
	 * @returns {Boolean} true if enabled
	 */
	getIsNewCompareLineEnabled(){
		return !this.dashboardApi.getGlassCoreSvc('.FeatureChecker').checkValue('explore', 'newCompareLine', 'false');
	}

	/**
	 * Any user of this feature can register a callback which will be called when the crosshairs move.
	 * The callback will receive a payload that includes:
	 *		- the widgetApi,
	 *		- the value that the crosshair has been moved to
	 *		- whether the mouse is down or not (ie: dragging)
	 * @param options - an object that contains (currently) 1 callback  'cbOnMoveCrosshairs'
	 */
	activate(options) {
		options && this._onMoveCrosshairsHandlers.push(options.cbOnMoveCrosshairs);
		if (!this._widgetApiOnMoveListener) {
			this._widgetApiOnMoveListener=this.widgetApi.on('visevent:onmove', this.onMove, this);
		}
	}

	/**
	 * API which implements "turn off crosshairs" (by making the feature stop listening to the onMove event.)
	 * This api also must "unrender the existing crosshair" (ie: clearCrosshairs()).
	 */
	deactivate() {
		if (this._widgetApiOnMoveListener) {
			this._widgetApiOnMoveListener.remove();
			this._widgetApiOnMoveListener=null;
		}
		return this.clearCrosshairs();
	}

	destroy() {
		this._onMoveCrosshairsHandlers=[];
		this.deactivate();
	}

	//VIEW methods (render support) ==========================================
	onMove(payload) {
		return this._onMove(payload.event, payload.target, payload.items, payload.mouseDown || payload.onHold);
	}

	_onMove(event, target, chartItems, mouseDown) {
		this._isDragging = mouseDown ? this._isDragging : false;
		let isGrabbingCompareLine = this._isDragging;
		// if we are about drag, check the event target is on the compare line
		if (mouseDown && !this._isDragging) {
			// Find if the event target's closest parent is the compare line. 
			// closest function is similar to jQuery $.closest()
			const closest = function(el, fn) {
				return el && (fn(el) ? el : closest(el.parentNode, fn));
			};
			closest(event.target, function(el) {
				const className = el.getAttribute && el.getAttribute('class');
				isGrabbingCompareLine = isGrabbingCompareLine || (className && className.indexOf('lineDecorations') > -1);
				return isGrabbingCompareLine;
			});
		}

		const shouldDrawCrosshairs = this.getIsNewCompareLineEnabled()? isGrabbingCompareLine : true;
		if (target && target.getValuesAtPoint && shouldDrawCrosshairs) {
			this._isDragging = true;
			/* offset is a pixel offset to ensure line is drawn slightly above/right of the pointer so that selection can still be done */
			const axisItemsAtPoint = target.getValuesAtPoint(event, 3 /*offset*/);
			const contField = axisItemsAtPoint && axisItemsAtPoint.find((axisItem) => axisItem.type === 'cont');
			if (contField && contField.value) {
				const value = contField.value;
				this._onMoveCrosshairsHandlers.forEach((onMoveCrosshairsHandler) => {
					onMoveCrosshairsHandler({ widgetApi: this.widgetApi, value: value, mouseDown: mouseDown });
				});
				return this.feature.renderCrosshairs(value);
			}
		}
		return Promise.resolve({});
	}

	/**
	 * renderCrosshairs at the specified value (for now, the value is single dimension)
	 * TODO: need to complete this part when vida story is implemented.
	 * @param value - the measure value to render the crosshairs at.
	 */
	renderCrosshairs(value) {
		const decoratorAPIs = this.widgetApi.getVisApi().getDecoratorAPIs();
		if (!decoratorAPIs || !decoratorAPIs.length) {
			return Promise.resolve({});
		}
		decoratorAPIs.forEach((decoratorAPI) => {
			this._decorateDataPointsNearCrosshairs(decoratorAPI, value);
		});
		//Note: Any decoratorAPI can be used to update decorations.
		return decoratorAPIs[0].updateDecorations();
	}

	clearCrosshairs() {
		let pointsCleared=null;
		const decoratorAPIs = this.widgetApi.getVisApi().getDecoratorAPIs();
		if (!decoratorAPIs || !decoratorAPIs.length) {
			return Promise.resolve({});
		}

		const itemToClearDecorations = this.feature.getItemToDecorate();
		if (itemToClearDecorations) {
			if (typeof this._prevValue !== 'undefined') {
				decoratorAPIs.forEach((decoratorAPI) => {
					pointsCleared = this._decoratePoints(decoratorAPI, itemToClearDecorations, this._prevValue, false);
					if(this.getIsNewCompareLineEnabled()){
						decoratorAPI.decorateItem(itemToClearDecorations.itemIndex, 'lines', null);
					} else {
						decoratorAPI.decorateItem(itemToClearDecorations.itemIndex, 'compareLine', null);
					}
				});
			}
		}
		// the previous value is not cleared so we can reuse it if user toggles the show/hide compare line
		//Note: Any decoratorAPI can be used to update decorations.
		return (pointsCleared) ? decoratorAPIs[0].updateDecorations() : Promise.resolve({});
	}

	getPreviousValue(){
		return this._prevValue;
	}

	setPreviousValue(value){
		this._prevValue = value;
	}

	//Read the result datapoints that are within the threshold and highlight the corresponding visualization datapoints.
	_decorateDataPointsNearCrosshairs(decoratorAPI, value) {
		value = value || 0;

		const itemToDecorate = this.feature.getItemToDecorate();

		var pointsCleared = null;
		var pointsDecorated = null;
		if (itemToDecorate) {
			if (this._prevValue) {
				pointsCleared = this._decoratePoints(decoratorAPI, itemToDecorate, this._prevValue, false);
			}
			pointsDecorated = this._decoratePoints(decoratorAPI, itemToDecorate, value, true);

			if(this.getIsNewCompareLineEnabled()){
				const Formatter = this.widgetApi.getVisApi().getFormatter();
				const formatSpec = itemToDecorate.format;
				const vaneCaption = (formatSpec) ? Formatter.format(value, formatSpec) : (Math.round(value * 100) / 100).toString();
				decoratorAPI.decorateItem(itemToDecorate.itemIndex, 'lines', {
					baseLine: { position: value, color: lineColor },
					grabber: { positionType: 'BEGIN' },
					vane: { caption: vaneCaption }
				});
			} else {
				const compareLineSpec = this._buildCompareLineSpec(itemToDecorate, pointsDecorated, value);
				decoratorAPI.decorateItem(itemToDecorate.itemIndex, 'compareLine', JSON.stringify(compareLineSpec));
			}
		}
		this._prevValue=value;
		return pointsCleared || pointsDecorated;
	}

	//Take the points decorated and produce a 'compareLinSpec' of up to MAX_CAPTIONS (4) captions and their formatted values.
	//If the number of points exceeds MAX_CAPTIONS, produce an "abbreviated form" pt1, pt2, then 'n of m items'.
	//Sample spec as specified by VIDA.
	//{
	//	"value" : 0.7799589760587813,
	//	"captions" : [["line1 blabla", "10000"], ["line2 column1", "20000"], ["line3 column1", "30000"], ["line4 column1", "40000"]],
	//	"footer" : "4 items of 9",
	//	"lineColor" : "rgb(234, 234, 234)",
	//	"lineStrokeWidth" : "8px"
	//}
	_buildCompareLineSpec(itemToDecorate, pointsDecorated, value) {
		var compareLineSpec={
			value: value,
			captions: this._buildLineCaptions(itemToDecorate, pointsDecorated),
			lineColor: lineColor,
			lineStrokeWidth: '1px'	//Shouldnt need to specify this but, if we don't, we don't get any text!
		};
		if (pointsDecorated.length > MAX_CAPTIONS) {
			//Add a footer if there are greater than MAX_CAPTIONS
			compareLineSpec.footer = StringResources.get('n1_of_n2_items', { n1:compareLineSpec.captions.length, n2: pointsDecorated.length } );
		}
		return compareLineSpec;
	}

	/** TODO Remove this function when the new PopOver is enabled */
	//Return a nested array of captions for the values near the line.
	//[[point1, value1], [point2, value2]... up to MAX_CAPTIONS (4)
	_buildLineCaptions(itemToDecorate, pointsDecorated) {
		var captionPoints=[];
		var captionArray=[];
		if (pointsDecorated && pointsDecorated.length) {
			const Formatter = this.widgetApi.getVisApi().getFormatter();
			const formatSpec=itemToDecorate.format && itemToDecorate.format.formatSpec;

			captionPoints = (pointsDecorated.length <= MAX_CAPTIONS) ? pointsDecorated : pointsDecorated.slice(0, MAX_CAPTIONS - 1);

			captionPoints.forEach((arrayEntry) => {
				let value = this.feature.getItemValue(arrayEntry.r, itemToDecorate.sourceItemUniqueId);
				captionArray.push(this._addCaption(arrayEntry.r, value, Formatter, formatSpec));
			});
		}
		return captionArray;
	}

	/**
	 * @return {Array} Returns a nested array of captions for the values from moving the compare line.
	 * Return value format: [[point1, value1], [point2, value2]
	 */
	getCrossHairMatchingPoints(value){
		const itemToDecorate = this.feature.getItemToDecorate();
		const decoratorAPIs = this.widgetApi.getVisApi().getDecoratorAPIs();
		let pointsDecorated = null;
		let captionPoints=[];
		let captionArray=[];
		if(!decoratorAPIs || !decoratorAPIs.length){
			return captionArray;
		}
		if(itemToDecorate){
			pointsDecorated = this._decoratePoints(decoratorAPIs[0], itemToDecorate, value, true);
		}
		if (pointsDecorated && pointsDecorated.length) {
			const Formatter = this.widgetApi.getVisApi().getFormatter();
			const formatSpec = itemToDecorate.format;

			captionPoints = pointsDecorated;

			captionPoints.forEach((arrayEntry) => {
				let value = this.feature.getItemValue(arrayEntry.r, itemToDecorate.sourceItemUniqueId);
				captionArray.push(this._addCaption(arrayEntry.r, value, Formatter, formatSpec));
			});
		}
		return captionArray;
	}

	/**
	 * Renders a popover with the given parameters when called.
	 * @param {Object} crossHairInfo - the info to show inside the popover
	 * @param {Object} renderNode - the node to render the popover to
	 * @param {Number} totalItems - the number of rows in the popover
	 * @param {Boolean} footerinfo.showFooter - true to show footer, false to hide footer
	 * @param {Number} footerinfo.footerHeight - height of the footer
	 * @param {Object} containerNode - the container node that the popover will be limited to
	 */
	renderPopOver(crossHairInfo, renderNode, totalItems, footerInfo, containerNode){
		renderNode && ReactDOM.render(<CrosshairsPopOver crossHairInfo={crossHairInfo} totalItems={totalItems} footerInfo={footerInfo} containerNode = {containerNode}/>, renderNode);
	}

	//Given a valuesArrayEntry (of form { value: value, row: rowIdx} ),
	//return the tuple at that row and its formatted value as an array of length 2.
	_addCaption(row, value, Formatter, formatSpec) {
		let tuple = this.feature.getRowTuple(row);
		let tupleDisplayValues = tuple && tuple.map((tuplePart) => tuplePart.d) || [];
		const valueStr = (formatSpec) ? Formatter.format(value, formatSpec) : value;
		return [tupleDisplayValues.join(' | '), valueStr];
	}

	//Set the highlighted decoration on the specified datapoints in the visualization.
	_decoratePoints(decoratorDataSet, itemToDecorate, value, highlightValue) {
		const dataPointsToHighlight = this.feature.getDataPoints(itemToDecorate.sourceItemUniqueId, value);
		dataPointsToHighlight.forEach((dataPointToHighlight) => {
			decoratorDataSet.decoratePoint(dataPointToHighlight.r, 'highlighted', highlightValue);
		});
		return dataPointsToHighlight;
	}
}

export default CrosshairsViewCtrl;


// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-crosshairs/src/js/CrosshairsViewCtrl.js